"use client";

import {
  Bell,
  MessageCircle,
  Plus,
  Settings,
  Upload,
  ChevronDown,
  PiIcon,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import eventBus from "../../utils/eventBus";
import { UserModelData } from "../../types";
import ArthereumIcon from "../../WebAppsSharedStuffs/components/ArtheumIcon";

const Button = ({
  onClick,
  children,
  className = "",
  variant = "default",
  size = "default",
  ...props
}: {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg";
  [key: string]: any;
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    default:
      "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md active:scale-95",
    outline:
      "border border-gray-200 bg-white hover:bg-gray-50 text-gray-900 hover:border-gray-300",
    ghost:
      "text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors",
  };

  const sizes = {
    default: "h-10 py-2 px-4 text-sm",
    sm: "h-9 px-3 text-sm",
    lg: "h-11 px-8",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const DesktopHeader = () => {
  const { t } = useTranslation();
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );
  const unreadCount = 0
  const unreadNotificationCount = 0
  const [piBalance, setPiBalance] = useState(
    userData?.user?.arthereumBalance ?? 0
  );


  useEffect(() => {
    const handleBalanceUpdate = (newBalance: number) => {
      setPiBalance(newBalance);
    };
    eventBus.on("balanceUpdate", handleBalanceUpdate);
    return () => {
      eventBus.off("balanceUpdate", handleBalanceUpdate);
    };
  }, []);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const navigate = useNavigate();
  const goToprofile = () => {
    navigate("/profile");
  };

  return (
    <header className="fixed top-0 left-64 right-0 z-49 border-b border-gray-200 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/80 shadow-sm">
      <div className="w-full flex h-16 items-center px-6 max-w-[1600px] mx-auto gap-6">
        {/* Left Section - Workspace */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div>test</div>
        </div>

        {/* Right Section - Actions + User */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg px-3 py-2 border border-amber-200/50 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105">
            <ArthereumIcon size="w-4 h-4" />
            <span className="font-semibold text-amber-900 text-sm">
              {piBalance?.toFixed(2)} ATH
            </span>
          </div>

          <Button
            onClick={() => navigate("/create-post")}
            variant="default"
            size="default"
            className="gap-2 ml-1"
          >
            <Plus className="h-4 w-4" />
            <span>{t("createPost")}</span>
          </Button>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-200 mx-2" />

          {/* Notifications */}
          <Button
            onClick={() => navigate("/notifications")}
            variant="ghost"
            size="default"
            className="relative gap-2"
          >
            <Bell className="h-4 w-4" />
            <span>{t("notifications")}</span>
            {unreadNotificationCount > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
              </div>
            )}
          </Button>

          {/* Uploads Button */}
          <Button
            onClick={() => navigate("/uploads")}
            variant="ghost"
            size="default"
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            <span>{t("uploads")}</span>
          </Button>

          {/* Settings Button */}
          <Button
            onClick={() => navigate("/settings")}
            variant="ghost"
            size="default"
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            <span>{t("settings")}</span>
          </Button>

          {/* Messages Button */}
          <Button
            onClick={() => navigate("/chats")}
            variant="ghost"
            size="default"
            className="relative gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            <span>{t("messages")}</span>
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                {unreadCount > 9 ? "9+" : unreadCount}
              </div>
            )}
          </Button>

          <button
            onClick={goToprofile}
            className="ml-2 flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
            title={t("profile")}
          >
            <div className="relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full border border-gray-200 group-hover:border-gray-300 transition-colors">
              <img
                className="aspect-square h-full w-full object-cover"
                src={userData?.user?.profileImage || "/placeholder.svg"}
                alt={userData?.user?.username ?? "User"}
              />
              {!userData?.user?.profileImage && (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 text-sm font-semibold">
                  {getInitials(userData?.user?.username?? "")}
                </div>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default DesktopHeader;
